import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AlertTriangle, CheckCircle, Clock, Plus, Receipt } from 'lucide-react';

import { formatarCentavos, paraCentavos } from '@/nucleo/dinheiro';
import { normalizarBusca } from '@/nucleo/utilitarios';
import { Button } from '@/ui/base/button';
import { DataTable } from '@/ui/DataTable';
import { DateRangePresetFilter } from '@/ui/DateRangePresetFilter';
import { resolveDateRange, type DateRangeValue } from '@/ui/dateRangePreset';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid } from '@/ui/StatsGrid';
import { StatusBadge } from '@/ui/StatusBadge';
import { type TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';

import { useCenarioDeTela } from '../_dev/cenarioDeTela';
import { EstadoSimulado } from '../_dev/EstadoSimulado';
import DADOS_SIMULADOS from './dados-simulados.json';
import { NovaCobrancaModal } from './NovaCobrancaModal';
import { type Cobranca, formatarDataBr, rotuloTipoServico, SITUACAO_PARA_BADGE } from './tipos';

/**
 * `/operacao/financeiro/cobrancas` — cobranças administrativas emitidas via
 * DAMSP (Documento de Arrecadação do Município de São Paulo).
 *
 * Não confundir com `/operacao/cobrancas` (Fiscalização): aquela é a cobrança
 * decorrente de penalidade; esta é a taxa de serviço administrativo (troca de
 * condutor, veículo, revisão de bancada etc.), com número DAMSP próprio.
 *
 * Dado simulado por decisão de produto (ver `../_dev/cenarioDeTela.ts`): a
 * API do módulo financeiro ainda não existe. Troque `DADOS_SIMULADOS` e o
 * `EstadoSimulado` por `dados.ts` quando o módulo nascer na API.
 */

const COLUNAS: TableViewColumn<Cobranca>[] = [
  {
    id: 'numeroDamsp',
    header: 'Nº DAMSP',
    accessorKey: 'numeroDamsp',
    cellClassName: 'font-mono text-sm',
    isCritical: true,
  },
  { id: 'credenciado', header: 'Credenciado', accessorKey: 'credenciado', isCritical: true },
  { id: 'tipo', header: 'Tipo de serviço', cell: (c) => rotuloTipoServico(c.tipoServico) },
  {
    id: 'valor',
    header: 'Valor',
    cell: (c) => <MoneyDisplay value={c.valor} />,
    headerClassName: 'text-right',
    cellClassName: 'text-right',
  },
  { id: 'emitidaEm', header: 'Emitida em', cell: (c) => formatarDataBr(c.emitidaEm) },
  {
    id: 'vencimento',
    header: 'Vencimento',
    cell: (c) => formatarDataBr(c.vencimento),
    linkedFilterKeys: ['vencimento'],
  },
  {
    id: 'situacao',
    header: 'Situação',
    cell: (c) => <StatusBadge status={SITUACAO_PARA_BADGE[c.situacao]} label={c.situacao} />,
    isStatusColumn: true,
    linkedFilterKeys: ['situacao'],
  },
];

const CAMPOS_FILTRO: FilterFieldConfig[] = [
  {
    key: 'busca',
    label: 'Busca',
    type: 'search',
    flex: true,
    placeholder: 'Buscar por nº DAMSP, credenciado ou serviço',
    ariaLabel: 'Buscar cobranças',
  },
  {
    key: 'situacao',
    label: 'Situação',
    type: 'select',
    width: 'w-full sm:w-[180px]',
    options: [
      { value: 'PENDENTE', label: 'Pendente' },
      { value: 'PAGA', label: 'Paga' },
      { value: 'VENCIDA', label: 'Vencida' },
      { value: 'CANCELADA', label: 'Cancelada' },
    ],
  },
];

/** Credenciados do formulário de "Nova cobrança" — os mesmos já usados na fixture, sem inventar novos nomes. */
const CREDENCIADOS_DISPONIVEIS = Array.from(
  new Map(
    (DADOS_SIMULADOS as Cobranca[]).map((c) => [
      c.credenciado,
      { nome: c.credenciado, documento: c.credenciadoDocumento },
    ]),
  ).values(),
);

export function CobrancasDamspPage() {
  const navigate = useNavigate();
  const cenario = useCenarioDeTela();

  const [cobrancasCriadas, setCobrancasCriadas] = useState<Cobranca[]>([]);
  const [criarAberto, setCriarAberto] = useState(false);

  const [busca, setBusca] = useState('');
  const [situacaoFiltro, setSituacaoFiltro] = useState('');
  const [vencimento, setVencimento] = useState<DateRangeValue>({ preset: 'ALL' });
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(10);

  const cobrancas = useMemo(
    () => (cenario === 'sucesso' ? [...cobrancasCriadas, ...(DADOS_SIMULADOS as Cobranca[])] : []),
    [cenario, cobrancasCriadas],
  );

  const pendentes = cobrancas.filter((c) => c.situacao === 'PENDENTE');
  const pagas = cobrancas.filter((c) => c.situacao === 'PAGA');
  const vencidas = cobrancas.filter((c) => c.situacao === 'VENCIDA');
  const totalPendenteCentavos = [...pendentes, ...vencidas].reduce(
    (soma, c) => soma + (paraCentavos(c.valor) ?? 0),
    0,
  );

  const { from: vencimentoDe, to: vencimentoAte } = useMemo(
    () => resolveDateRange(vencimento),
    [vencimento],
  );

  const cobrancasFiltradas = useMemo(() => {
    return cobrancas.filter((c) => {
      if (situacaoFiltro && c.situacao !== situacaoFiltro) return false;
      if (vencimentoDe && c.vencimento < vencimentoDe) return false;
      if (vencimentoAte && c.vencimento > vencimentoAte) return false;
      if (busca) {
        const alvo = normalizarBusca(busca);
        const campo = normalizarBusca(`${c.numeroDamsp} ${c.credenciado} ${c.tipoServico}`);
        if (!campo.includes(alvo)) return false;
      }
      return true;
    });
  }, [cobrancas, situacaoFiltro, vencimentoDe, vencimentoAte, busca]);

  const totalPaginas = Math.max(1, Math.ceil(cobrancasFiltradas.length / porPagina));
  const paginaEfetiva = Math.min(pagina, totalPaginas);
  const cobrancasPaginadas = cobrancasFiltradas.slice(
    (paginaEfetiva - 1) * porPagina,
    paginaEfetiva * porPagina,
  );

  const hasActiveFilters = !!busca || !!situacaoFiltro || vencimento.preset !== 'ALL';

  const tableView = useTableViewPreferences({
    contextKey: 'financeiro:cobrancas:list',
    columns: COLUNAS,
    filterValues: {
      situacao: situacaoFiltro,
      vencimento: vencimento.preset === 'ALL' ? '' : vencimento.preset,
    },
  });

  const limparFiltros = () => {
    setBusca('');
    setSituacaoFiltro('');
    setVencimento({ preset: 'ALL' });
    setPagina(1);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cobranças DAMSP"
        description="Gestão das cobranças DAMSP emitidas para os credenciados (taxas administrativas, substituições, revisão de bancada, multas)."
      >
        <Button onClick={() => setCriarAberto(true)}>
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Nova cobrança</span>
        </Button>
      </PageHeader>

      <NovaCobrancaModal
        open={criarAberto}
        onOpenChange={setCriarAberto}
        credenciados={CREDENCIADOS_DISPONIVEIS}
        onCriar={(nova) => {
          setCobrancasCriadas((atual) => [nova, ...atual]);
          setPagina(1);
        }}
      />

      <StatsGrid
        stats={[
          { title: 'Pendentes', value: pendentes.length, icon: Clock },
          { title: 'Pagas', value: pagas.length, icon: CheckCircle },
          { title: 'Vencidas', value: vencidas.length, icon: AlertTriangle },
          {
            title: 'Total Pendente',
            value: formatarCentavos(totalPendenteCentavos),
            icon: Receipt,
          },
        ]}
      />

      <FilterBar
        fields={CAMPOS_FILTRO}
        values={{ busca, situacao: situacaoFiltro }}
        onChange={(chave, valor) => {
          if (chave === 'busca') {
            setBusca(valor);
            setPagina(1);
          }
          if (chave === 'situacao') {
            setSituacaoFiltro(valor);
            setPagina(1);
          }
        }}
        onClear={limparFiltros}
        hasActiveFilters={hasActiveFilters}
      >
        <DateRangePresetFilter
          label="Vencimento"
          value={vencimento}
          onChange={(proximo) => {
            setVencimento(proximo);
            setPagina(1);
          }}
        />
      </FilterBar>

      <EstadoSimulado
        cenario={cenario}
        descricao="as cobranças DAMSP"
        colunasDoEsqueleto={COLUNAS.length}
      >
        <DataTable
          columns={tableView.orderedColumns}
          personalizacao={tableView}
          data={cobrancasPaginadas}
          total={cobrancasFiltradas.length}
          page={paginaEfetiva}
          perPage={porPagina}
          onPageChange={setPagina}
          onPerPageChange={(novoPorPagina) => {
            setPorPagina(novoPorPagina);
            setPagina(1);
          }}
          onRowClick={(c) => navigate(`/operacao/financeiro/cobrancas/${c.id}`)}
          emptyTitle="Nenhuma cobrança encontrada"
          emptyDescription="Cobranças DAMSP emitidas para os credenciados aparecem aqui."
          minWidth="760px"
          ariaLabel="Cobranças DAMSP"
          skeletonColumns={COLUNAS.length}
        />
      </EstadoSimulado>
    </div>
  );
}

export default CobrancasDamspPage;
